import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { SlotPicker, useSlots } from "./SlotPicker";
import { Loader2, ArrowRight, X, Zap, Sparkles } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { trackConversion } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PREFS = ["Mattina", "Pomeriggio", "Indifferente"];
const TREATMENTS = [
  { id: "parodontale", icon: Zap, title: "Visita parodontale", text: "Gengive che sanguinano o denti che si muovono: diagnosi e cura laser." },
  { id: "igiene_gbt", icon: Sparkles, title: "Seduta di igiene GBT", text: "Pulizia professionale delicata con protocollo GBT certificato." },
];
const INITIAL = { trattamento: "parodontale", nome: "", cognome: "", telefono: "", email: "", preferenza: "Indifferente", note: "", newsletter: false, consenso: false };
const inputCls =
  "w-full bg-transparent border-0 border-b border-[#1B3B28]/20 px-0 py-3 text-lg text-[#1B3B28] placeholder:text-[#526356]/50 focus:outline-none focus:border-[#3E6B48] transition-colors";
const anxietyLabel = (v) => (v <= 2 ? "Tranquillo" : v <= 5 ? "Un po' teso" : v <= 8 ? "Piuttosto in ansia" : "Molto spaventato");

const Field = ({ label, children }) => (
  <label className="block">
    <span className="eyebrow text-[#526356]">{label}</span>
    {children}
  </label>
);

const TreatmentPicker = ({ value, onChange }) => (
  <div className="sm:col-span-2" role="radiogroup" aria-label="Cosa desideri prenotare?">
    <span className="eyebrow text-[#526356]">Cosa desideri prenotare?</span>
    <div className="mt-3 grid sm:grid-cols-2 gap-3">
      {TREATMENTS.map(({ id, icon: Icon, title, text }) => {
        const on = value === id;
        return (
          <button type="button" role="radio" aria-checked={on} key={id} onClick={() => onChange(id)} data-testid={`booking-treatment-${id}`}
            className={`relative text-left rounded-2xl p-5 border transition-[background-color,border-color,transform] duration-300 active:scale-[0.98] ${on ? "bg-[#1B3B28] border-[#1B3B28] text-[#FCFBF7]" : "bg-transparent border-[#1B3B28]/15 text-[#1B3B28] hover:border-[#3E6B48]"}`}>
            <span className={`grid place-items-center w-9 h-9 rounded-full ${on ? "bg-[#86C89B] text-[#0F2317]" : "bg-[#E6ECE4] text-[#1B3B28]"}`}><Icon size={16} /></span>
            <span className="block f-serif text-2xl leading-tight mt-4">{title}</span>
            <span className={`block text-sm leading-snug mt-1.5 ${on ? "text-[#FCFBF7]/70" : "text-[#526356]"}`}>{text}</span>
            <span className={`absolute top-5 right-5 w-4 h-4 rounded-full border-2 ${on ? "border-[#86C89B] bg-[#86C89B] shadow-[inset_0_0_0_3px_#1B3B28]" : "border-[#1B3B28]/25"}`} />
          </button>
        );
      })}
    </div>
  </div>
);

export const BookingForm = ({ symptoms, setSymptoms, onDone, fixedTreatment }) => {
  const [params] = useSearchParams();
  const [form, setForm] = useState(() => ({ ...INITIAL, trattamento: fixedTreatment || (params.get("trattamento") === "gbt" ? "igiene_gbt" : "parodontale") }));
  const gbt = form.trattamento === "igiene_gbt";
  const [ansia, setAnsia] = useState([5]);
  const [loading, setLoading] = useState(false);
  const [slot, setSlot] = useState(null);
  const slots = useSlots();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consenso) return toast.error("Serve il consenso al trattamento dei dati per ricontattarti.");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, { ...form, sintomi: symptoms, ansia: ansia[0], requested_date: slot?.date ?? null, requested_time: slot?.time ?? null });
      toast.success("Richiesta inviata. Ti ricontatteremo con calma, a breve.");
      trackConversion("prenotazione", { tipo: gbt ? "igiene_gbt" : "prima_visita", con_orario: slot ? "si" : "no" });
      onDone({ name: form.nome, email: form.email, emailSent: data.patient_email_sent, trattamento: form.trattamento, slot });
      setForm(INITIAL);
      setSlot(null);
      setSymptoms([]);
    } catch (err) {
      if (err.response?.status === 409) { setSlot(null); slots.load(); return toast.error(err.response.data.detail); }
      toast.error("Qualcosa non ha funzionato. Riprova o scrivici su WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8" data-testid="booking-form">
      {!fixedTreatment && <TreatmentPicker value={form.trattamento} onChange={(t) => setForm((f) => ({ ...f, trattamento: t }))} />}
      <Field label="Nome"><input required value={form.nome} onChange={set("nome")} className={inputCls} placeholder="Maria" data-testid="booking-input-nome" /></Field>
      <Field label="Cognome"><input required value={form.cognome} onChange={set("cognome")} className={inputCls} placeholder="Rossi" data-testid="booking-input-cognome" /></Field>
      <Field label="Telefono"><input required type="tel" minLength={6} value={form.telefono} onChange={set("telefono")} className={inputCls} placeholder="+39 ..." data-testid="booking-input-telefono" /></Field>
      <Field label="Email"><input required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="nome@email.it" data-testid="booking-input-email" /></Field>

      <SlotPicker value={slot} onChange={setSlot} slots={slots} />

      {!slot && <div className="sm:col-span-2">
<span className="eyebrow text-[#526356]">Quando preferisci essere richiamato?</span>
        <div className="mt-3 flex flex-wrap gap-2">
          {PREFS.map((p) => (
            <button type="button" key={p} onClick={() => setForm((f) => ({ ...f, preferenza: p }))} data-testid={`booking-pref-${p.toLowerCase()}`}
              className={`rounded-full px-5 py-2.5 text-sm border transition-colors ${form.preferenza === p ? "bg-[#1B3B28] text-[#FCFBF7] border-[#1B3B28]" : "border-[#1B3B28]/15 text-[#1E2922] hover:border-[#3E6B48]"}`}>
              {p}
            </button>
          ))}
        </div>
      </div>}

      <div className="sm:col-span-2">
        <div className="flex items-baseline justify-between">
          <span className="eyebrow text-[#526356]">Quanta paura hai del dentista?</span>
          <span className="f-serif italic text-xl text-[#3E6B48]" data-testid="booking-anxiety-label">{ansia[0]}/10 · {anxietyLabel(ansia[0])}</span>
        </div>
        <Slider value={ansia} onValueChange={setAnsia} min={0} max={10} step={1} className="mt-5" data-testid="booking-anxiety-slider" />
        <p className="text-xs text-[#526356] mt-3">Ce lo dici per prepararci ad accoglierti nel modo giusto. Nessun giudizio.</p>
      </div>

      {symptoms.length > 0 && (
        <div className="sm:col-span-2" data-testid="booking-symptoms">
          <span className="eyebrow text-[#526356]">Sintomi segnalati</span>
          <div className="mt-3 flex flex-wrap gap-2">
            {symptoms.map((s) => (
              <button type="button" key={s} onClick={() => setSymptoms((p) => p.filter((x) => x !== s))}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#E6ECE4] text-[#1B3B28] px-3 py-1.5 text-xs">
                {s} <X size={12} />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="sm:col-span-2">
        <Field label="Note (facoltative)">
          <textarea rows={2} value={form.note} onChange={set("note")} className={`${inputCls} resize-none`} placeholder="Raccontaci qualcosa, se vuoi" data-testid="booking-input-note" />
        </Field>
      </div>
      <div className="sm:col-span-2 -mt-2 flex items-start gap-3">
        <Checkbox id="newsletter" checked={form.newsletter} onCheckedChange={(v) => setForm((f) => ({ ...f, newsletter: !!v }))} data-testid="booking-newsletter-checkbox" className="mt-0.5 h-5 w-5" />
        <label htmlFor="newsletter" className="text-sm text-[#526356]">Facoltativo: voglio ricevere la newsletter con consigli per gengive e denti sani (puoi disiscriverti con un clic).</label>
      </div>
      <div className="sm:col-span-2 -mt-2 flex items-start gap-3">
        <Checkbox id="consenso" checked={form.consenso} onCheckedChange={(v) => setForm((f) => ({ ...f, consenso: !!v }))} data-testid="booking-consent-checkbox" className="mt-0.5 h-5 w-5" />
        <label htmlFor="consenso" className="text-sm text-[#526356]">
          Acconsento al trattamento dei miei dati, inclusi quelli relativi alla salute, per essere ricontattato in merito alla prenotazione, come descritto nella{" "}
          <a href="https://www.iubenda.com/privacy-policy/67116616" target="_blank" rel="noopener noreferrer" className="iubenda-nostyle text-[#1B3B28] underline underline-offset-2" data-testid="booking-privacy-link">Privacy Policy</a>.
        </label>
      </div>
      <div className="sm:col-span-2">
        <button type="submit" disabled={loading} data-testid="booking-submit-btn"
          className="group inline-flex items-center gap-3 rounded-full bg-[#1B3B28] text-[#FCFBF7] pl-8 pr-2 py-2 hover:bg-[#3E6B48] transition-colors disabled:opacity-60">
          {loading ? "Invio in corso" : gbt ? "Richiedi la seduta GBT" : "Richiedi la prima visita"}
          <span className="grid place-items-center w-11 h-11 rounded-full bg-[#86C89B] text-[#0F2317] transition-transform duration-500 group-hover:translate-x-1">
            {loading ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
          </span>
        </button>
      </div>
    </form>
  );
};
